import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import { useEffect, useState } from "react";
import "../../styles/components/orders/_businessOrdersTableComponent.scss";
import AlertService from "../../services/alerts/alert.service";
import orderActions from "../../store/actions/orders/order.actions";
import useAccount from "../../hooks/useAccount";
import usePagination from "../../hooks/usePagination";
import { Link } from "react-router-dom";
import shoppingCartActions from "../../store/actions/shoppingCart/shoppingCart.actions";
import productActions from "../../store/actions/items/item.actions";
import LoaderComponent from "../general/LoaderComponent";
import datesHelper from "../../helpers/dates.helper";
import TableElementComponent from "../general/elements/TableElementComponent";
import InfiniteScroll from "react-infinite-scroll-component";
import useDelayedFlag from "../../hooks/useDelayedFlag";
import { SkeletonTable } from "../general/skeleton";

// 0005: order source attribution. The backend sends numeric placedByRole (0 Business,
// 1 Administrator, 2 Agent — no JsonStringEnumConverter is registered) plus placedByName
// ("FirstName LastName", Email fallback) for roles 1/2. Business self-orders carry null and
// show the self label here. Unresolved/null name falls back to "לא ידוע".
const formatPlacedBy = (row) => {
  switch (row.placedByRole) {
    case 2:
      return `סוכן: ${row.placedByName || "לא ידוע"}`;
    case 1:
      return `מנהל: ${row.placedByName || "לא ידוע"}`;
    default:
      return "הלקוח עצמו";
  }
};

const BusinessOrdersTableComponent = ({ orderActions, shoppingCartActions, productActions }) => {
  const { isLoggedIn, userInfo } = useAccount();
  // 0018: isLoading now drives ONLY the duplicate-order action spinner (an action, not a data
  // load). The initial orders fetch is tracked separately so it can show a skeleton table.
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const showSkeleton = useDelayedFlag(isInitialLoading);

  // Wait for userInfo to be available before fetching orders
  useEffect(() => {
    if (userInfo?.id) {
      setIsInitialLoading(true); // Ensure loading state is set before fetching
      fetchNextPage().finally(() => setIsInitialLoading(false)); // Set loading to false after fetching
    }
  }, [userInfo]); // Run only when userInfo is available

  const {
    items: orders,
    fetchNextPage,
    hasNextPage,
    totalCount,
  } = usePagination({
    queryKey: ["business-orders"],
    fetchFunction: async (pageParam) => {
      const params = {
        pageNumber: pageParam,
        pageSize: 10,
        getAll: userInfo.userType === "Administrator" ? true : false,
        userId: userInfo.id,
      };
      const response = await orderActions.fetchOrders(params);
      return response;
    },
  });

  const handleDownload = (url) => {
    if (!url) {
      AlertService.info("לא נמצא מסמך הזמנה להצגה", "אנה נסה שנית מאוחר יותר או צור קשר עם בית העסק");
      return;
    }

    // Create a hidden anchor element
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", ""); // Ensures it's recognized as a download
    link.target = "_blank"; // Opens in a new tab (if applicable)
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDuplicateOrder = async (order) => {
    if (!order || !order.orderItems || order.orderItems.length === 0) {
      AlertService.error("שגיאה", "אין מוצרים להזמנה זו לשכפל.");
      return;
    }

    setIsLoading(true);

    let missingItems = [];
    let updatedItems = [];

    for (const item of order.orderItems) {
      try {
        // Fetch latest product details
        const latestProduct = await productActions.getProductById(item.rivhit_ItemId);
        if (!latestProduct) {
          missingItems.push(item.productName);
          continue; // Skip missing items
        }
        // Check for price changes
        const priceChanged = item.unitPrice !== latestProduct.salePriceInNIS;
        const itemPackSize = latestProduct.minOrderQuantity <= 1 ? 1 : latestProduct.minOrderQuantity;
        // Format the item for the cart
        const cartItem = {
          id: latestProduct.id,
          itemId: latestProduct.itemId,
          itemDescription: latestProduct.itemDescription,
          quantity: item.quantity,
          salePriceInNIS: latestProduct.salePriceInNIS, // Use latest price
          pictureUrl: latestProduct.pictureUrl, // Ensure correct image
          itemPackSize: itemPackSize,
        };
        // Dispatch to Redux to update the cart
        shoppingCartActions.addToCart(cartItem);

        if (priceChanged) {
          updatedItems.push({
            oldPrice: item.unitPrice,
            newPrice: latestProduct.salePriceInNIS,
            productName: latestProduct.itemDescription,
          });
        }
      } catch (error) {
        console.error(`Error fetching product ${item.productId}:`, error);
        missingItems.push(item.productName);
      }
    }

    setIsLoading(false);

    // Generate HTML message for missing and updated items
    let messageHTML = "";

    if (missingItems.length > 0) {
      messageHTML += `
        <div style="color: red; font-weight: bold; margin-bottom: 10px;">
          <i class="fa-solid fa-triangle-exclamation"></i> המוצרים הבאים אינם זמינים:
        </div>
        <ul style="color: red; list-style-type: none; padding: 0;">
          ${missingItems.map((item) => `<li>❌ ${item}</li>`).join("")}
        </ul>
        <hr>
      `;
    }

    if (updatedItems.length > 0) {
      messageHTML += `
        <div style="color: orange; font-weight: bold; margin-bottom: 10px;">
          <i class="fa-solid fa-tag"></i> שינויי מחירים:
        </div>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr>
              <th style="border: 1px solid #ddd; padding: 5px;">שם המוצר</th>
              <th style="border: 1px solid #ddd; padding: 5px;">מחיר קודם</th>
              <th style="border: 1px solid #ddd; padding: 5px;">מחיר חדש</th>
            </tr>
          </thead>
          <tbody>
            ${updatedItems
              .map(
                (item) => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 5px;">${item.productName}</td>
                <td style="border: 1px solid #ddd; padding: 5px; color: red;">${item.oldPrice}₪</td>
                <td style="border: 1px solid #ddd; padding: 5px; color: #663333;">${item.newPrice}₪</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        <hr>
      `;
    }

    // Show final success alert with all details
    AlertService.htmlAlert(`הזמנה מספר: ${order.orderNumber} שוכפלה בהצלחה`, messageHTML || "כל המוצרים נוספו בהצלחה לעגלת הקניות.", "success");
  };

  const getOrdersTable = () => {
    const columns = [
      { label: "מספר הזמנה", field: "orderNumber" },
      { label: "תאריך הזמנה", field: "itemDescription", render: (value, row) => <span>{datesHelper.formatDate(row.orderDate)}</span> },
      { label: 'סה"כ כולל מע"מ', field: "totalPrice" },
      // 0005: who placed the order. Shown to all roles (incl. a Business user's own history,
      // so they can see an order an agent/admin placed on their behalf — Q2).
      { label: "בוצע על ידי", field: "placedBy", render: (value, row) => <span>{formatPlacedBy(row)}</span> },
    ];

    // M4: an Agent (like an Administrator) manages multiple customers, so show which
    // customer each order belongs to. The order set itself is scoped server-side.
    if (userInfo?.userType === "Administrator" || userInfo?.userType === "Agent") {
      columns.push(
        { label: "לקוח", field: "userName", render: (value, row) => <span>{row.customer.lastName}</span> },
        { label: "טלפון", field: "userPhone", render: (value, row) => <span>{row.customer.phone}</span> },
        { label: "אימייל", field: "userPhone", render: (value, row) => <span>{row.customer.email}</span> }
      );
    }

    const actions = [
      {
        label: "שכפל",
        icon: "fa-solid fa-copy",
        className: "greenButton",
        onClick: (order) => handleDuplicateOrder(order),
      },
      {
        label: "הצג",
        icon: "fa-regular fa-eye",
        className: "greenButton",
        onClick: (order) => handleDownload(order.rivhit_DocumentLink),
      },
    ];
    return <TableElementComponent columns={columns} data={orders} actions={actions} />;
  };

  const getNoOrdersElement = () => (
    <div className="noItemsContainer">
      <h2>לא נמצאו הזמנות</h2>
    </div>
  );

  // The duplicate-order ACTION keeps its full-screen spinner (not a data load → not a skeleton).
  if (isLoading) return <LoaderComponent />;

  // 0018: orders table has 4 data columns (Business) or 7 (Admin/Agent), plus one actions column.
  const isAdminOrAgent = userInfo?.userType === "Administrator" || userInfo?.userType === "Agent";
  const orderColumnCount = (isAdminOrAgent ? 7 : 4) + 1;

  return (
    <div className="ordersTableContainer">
      <h1>היסטוריית הזמנות</h1>
      {/* 0018: skeleton table on the INITIAL load only; the InfiniteScroll page-append loader
          below stays a text loader. The ~120ms gate avoids a flash on a cached (60s) re-open. */}
      {isInitialLoading ? (
        showSkeleton && (
          <div className="productListContainer">
            <SkeletonTable rows={8} cols={orderColumnCount} />
          </div>
        )
      ) : orders?.length > 0 ? (
        <InfiniteScroll dataLength={orders.length} next={fetchNextPage} hasMore={hasNextPage} loader={<p>טוען הזמנות נוספות...</p>}>
          <div className="productListContainer">{getOrdersTable()}</div>
        </InfiniteScroll>
      ) : (
        getNoOrdersElement()
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  orders: state.orderReducer.orders?.items, // Subscribe to orders state from the reducer
});

const mapDispatchToProps = (dispatch) => {
  return {
    orderActions: bindActionCreators(orderActions, dispatch),
    shoppingCartActions: bindActionCreators(shoppingCartActions, dispatch),
    productActions: bindActionCreators(productActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BusinessOrdersTableComponent);
